import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, Upload, FileText, TestTube, Stethoscope, Pill } from 'lucide-react';
import { mockTimelineRecords, familyMembers } from '../data/mockData';
export function RecordsTimelineScreen() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const [activePerson, setActivePerson] = useState(familyMembers[0].id);
  const filters = ['All', 'Visits', 'Prescriptions', 'Lab Results', 'Files'];
  const getIcon = (type: string) => {
    switch (type) {
      case 'Prescription':
        return <Pill className="w-4 h-4 text-purple-600" data-id="element-1499" />;
      case 'Lab Result':
        return <TestTube className="w-4 h-4 text-teal-600" data-id="element-1500" />;
      case 'Visit':
        return <Stethoscope className="w-4 h-4 text-blue-600" data-id="element-1501" />;
      default:
        return <FileText className="w-4 h-4 text-slate-600" data-id="element-1502" />;
    }
  };
  const getIconBg = (type: string) => {
    switch (type) {
      case 'Prescription':
        return 'bg-purple-100 border-purple-200';
      case 'Lab Result':
        return 'bg-teal-100 border-teal-200';
      case 'Visit':
        return 'bg-blue-100 border-blue-200';
      default:
        return 'bg-slate-100 border-slate-200';
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="flex flex-col min-h-screen bg-slate-50" data-id="element-1503">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10" data-id="element-1504">
        <div className="flex items-center justify-between mb-4" data-id="element-1505">
          <div className="flex items-center gap-3" data-id="element-1506">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-1507">
              <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-1508" />
            </button>
            <h1 className="text-xl font-bold text-slate-900" data-id="element-1509">
              Medical Records
            </h1>
          </div>
          <div className="flex gap-2" data-id="element-1510">
            <button className="p-2 text-slate-600 hover:bg-slate-50 rounded-full" data-id="element-1511">
              <Upload className="w-5 h-5" data-id="element-1512" />
            </button>
            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-full" data-id="element-1513">
              <Share2 className="w-5 h-5" data-id="element-1514" />
            </button>
          </div>
        </div>

        <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-2" data-id="element-1515">
          {familyMembers.map(member => <button key={member.id} onClick={() => setActivePerson(member.id)} className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activePerson === member.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1516">
              {member.name.split(' ')[0]}
            </button>)}
        </div>
      </div>

      <div className="px-6 py-4" data-id="element-1517">
        <div className="flex overflow-x-auto hide-scrollbar gap-2 mb-6" data-id="element-1518">
          {filters.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-3 py-1 rounded-lg text-sm whitespace-nowrap transition-colors ${activeFilter === filter ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-500 hover:bg-slate-100'}`} data-id="element-1519">
              {filter}
            </button>)}
        </div>

        <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-1520">
          {mockTimelineRecords.map(record => <div key={record.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group cursor-pointer" onClick={() => record.type === 'Prescription' ? navigate('/prescription') : null} data-id="element-1521">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 border-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 ${getIconBg(record.type)}`} data-id="element-1522">
                {getIcon(record.type)}
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-1523">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-1524">
                  <div className="text-xs font-medium text-slate-500" data-id="element-1525">
                    {record.type}
                  </div>
                  <time className="text-xs font-medium text-slate-400" data-id="element-1526">
                    {record.date}
                  </time>
                </div>
                <h3 className="font-bold text-slate-900 mb-1" data-id="element-1527">
                  {record.title}
                </h3>
                <div className="flex items-center justify-between" data-id="element-1528">
                  <p className="text-sm text-slate-500" data-id="element-1529">{record.provider}</p>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full" data-id="element-1530">
                    {record.status}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </motion.div>;
}